import type { MittwaldAPIV2Client } from "@mittwald/api-client-NEXT";
import { assertOneOfStatus, assertStatus } from "@mittwald/api-client-NEXT";
import { checkForValidationErrors } from "../../../base/ValidationError";
import type { UserBehaviors } from "./types.js";

export const apiUserBehaviors = (
  client: MittwaldAPIV2Client,
): UserBehaviors => ({
  find: async (userId, accessToken) => {
    const response = await client.user.getUser({
      userId,
      headers: accessToken ? { "x-access-token": accessToken } : undefined,
    });
    if (response.status === 200) {
      return response.data;
    }
    // eslint-disable-next-line @typescript-eslint/ban-ts-comment
    // @ts-ignore
    assertOneOfStatus(response, [400, 403, 404]);
  },

  createAvatarUploadToken: async (userId) => {
    const response = await client.user.requestAvatarUpload({ userId });
    assertStatus(response, 200);
    return {
      token: response.data.refId,
      rules: response.data.rules,
    };
  },

  getPasswordUpdatedAt: async () => {
    const response = await client.user.getPasswordUpdatedAt({});

    assertStatus(response, 200);

    return response.data;
  },

  updatePersonalInformation: async (userId, data) => {
    const response = await client.user.updatePersonalInformation({
      userId,
      data: { person: data },
    });

    assertStatus(response, 204);
  },

  addPhoneNumber: async (userId, phoneNumber) => {
    const response = await client.user.addPhoneNumber({
      userId,
      data: { phoneNumber },
    });

    assertStatus(response, 204);
  },

  verifyPhoneNumber: async (userId, data) => {
    const response = await client.user.verifyPhoneNumber({ userId, data });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertStatus(response, 204);
  },

  removePhoneNumber: async (userId) => {
    const response = await client.user.removePhoneNumber({ userId });

    assertStatus(response, 204);
  },

  updateEmail: async (email) => {
    const response = await client.user.changeEmail({ data: { email } });

    assertStatus(response, 204);
  },

  verifyEmail: async (data) => {
    const response = await client.user.verifyEmail({ data });

    assertStatus(response, 204);
  },

  removeAvatar: async (userId) => {
    const response = await client.user.removeAvatar({ userId });

    assertStatus(response, 204);
  },

  updatePassword: async (data) => {
    const response = await client.user.changePassword({ data });

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertOneOfStatus(response, [200, 202]);

    if (response.status === 202) {
      return "mfaRequired";
    }

    return response.data;
  },

  resetPassword: async (email) => {
    const response = await client.user.initPasswordReset({ data: { email } });

    assertStatus(response, 201);
  },

  confirmPasswordReset: async (data) => {
    const response = await client.user.confirmPasswordReset({ data });

    assertStatus(response, 204);
  },

  authenticate: async (data) => {
    const response = await client.user.authenticate({ data });

    assertOneOfStatus(response, [200, 202]);

    if (response.status === 202) {
      return "mfaRequired";
    }

    return response.data;
  },

  logout: async () => {
    const response = await client.user.logout();

    assertStatus(response, 204);
  },

  register: async (data) => {
    const response = await client.user.register({ data });

    assertStatus(response, 201);

    return { id: response.data.userId };
  },

  verifyRegistration: async (data) => {
    const response = await client.user.verifyRegistration({ data });

    assertStatus(response, 200);
  },

  resendVerificationEmail: async (data) => {
    const response = await client.user.resendVerificationEmail({ data });

    assertStatus(response, 204);
  },

  delete: async (data) => {
    const response = await client.user.deleteUser({ data });

    if (response.status === 409) {
      return "isLastOwner";
    }

    const validationErrors = checkForValidationErrors(response);
    if (validationErrors) {
      return validationErrors;
    }

    assertOneOfStatus(response, [200, 202]);
  },

  refreshSession: async (refreshToken) => {
    const response = await client.user.refreshSession({
      data: { refreshToken },
    });
    assertStatus(response, 200);

    return response.data;
  },
});
